import { h } from "../kit/dom.ts";
import { keyHint } from "./settings.ts";

export interface CarrancaRow {
  sessionId: string;
  name: string;
}

const ALLY_HINT = "Paga e o aliado volta aqui, a qualquer hora (a Lápide some; os Pertences ficam).";
const SOLO_HINT = "Sozinho, você mesmo paga com a sua bolsa e volta na Estátua da Fênix mais perto da Lápide (os Pertences ficam). Desistir é derrota.";

/**
 * Carranca (doc 02 §17): lista os aliados mortos com o preço de hoje; o botão paga e revive ali.
 * O servidor confere distância, saldo e se o aliado ainda está morto. Sozinho e morto (F54), o mesmo
 * painel oferece pagar a própria volta ou desistir, e não fecha enquanto a escolha está aberta.
 */
export class CarrancaPanel {
  readonly element: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly list: HTMLElement;
  private readonly empty: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly closeHint: HTMLElement;
  private readonly giveUpButton: HTMLButtonElement;
  /** O primeiro clique em "Desistir" só arma; o segundo desiste (a derrota é na hora). */
  private giveUpArmed = false;
  private key = "";
  private readonly pay: (target: string) => void;
  /** Carranca aberta (id da estrutura). */
  id: string | undefined;
  /** Aberto para a própria volta, sozinho e morto (F54). */
  solo = false;

  constructor(parent: HTMLElement, pay: (target: string) => void, giveUp: () => void) {
    this.pay = pay;
    this.purse = h("p", { class: "bolsa" });
    this.list = h("div", { class: "craft-list" });
    this.empty = h("p", { class: "hint" }, "Nenhum aliado morto agora.");
    this.hint = h("p", { class: "hint" }, ALLY_HINT);
    this.closeHint = keyHint("interact", "{} fecha");
    this.giveUpButton = h("button", { type: "button", id: "carranca-desistir" }, "Desistir") as HTMLButtonElement;
    this.giveUpButton.addEventListener("click", () => {
      if (!this.giveUpArmed) {
        this.giveUpArmed = true;
        this.giveUpButton.textContent = "Desistir mesmo? Clique de novo";
        return;
      }
      giveUp();
    });
    this.giveUpButton.hidden = true;
    this.element = h(
      "section",
      { id: "carranca", class: "panel crafting trade", "aria-label": "Estátua da Fênix" },
      h("h2", {}, "Estátua da Fênix"),
      this.hint,
      this.purse,
      this.list,
      this.empty,
      this.giveUpButton,
      this.closeHint,
    );
    this.element.hidden = true;
    parent.appendChild(this.element);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  show(id: string | undefined): void {
    this.id = id;
    this.setSolo(false);
    this.element.hidden = id === undefined;
    this.key = "";
  }

  /** Sozinho e morto (F54): a própria volta (paga com a bolsa) ou desistir, sem Carranca por perto. */
  showSolo(): void {
    this.id = undefined;
    this.setSolo(true);
    this.element.hidden = false;
    this.key = "";
  }

  /** `left`: sozinho, os segundos que faltam para decidir (o servidor desiste por você no fim). */
  update(rows: readonly CarrancaRow[], price: number, coins: number, left?: number): void {
    // O prazo muda a cada segundo: só o texto da dica, no lugar. Redesenhar a lista trocaria o botão
    // sob o ponteiro e perderia o clique.
    if (this.solo) {
      const hint = left !== undefined ? `${SOLO_HINT} Decida em ${left} s.` : SOLO_HINT;
      if (this.hint.textContent !== hint) this.hint.textContent = hint;
    }
    const key = `${this.solo}|${price}|${coins}|${rows.map((r) => `${r.sessionId}:${r.name}`).join(",")}`;
    if (key === this.key) return;
    this.key = key;
    this.purse.textContent = `Bolsa: ${coins} ${coins === 1 ? "moeda" : "moedas"} · reviver custa ${price}`;
    this.empty.hidden = rows.length > 0;
    this.list.replaceChildren(
      ...rows.map((r) => {
        const button = h("button", { type: "button", "data-alvo": r.sessionId }, `Reviver (${price} moedas)`);
        button.disabled = coins < price;
        button.addEventListener("click", () => this.pay(r.sessionId));
        return h("div", { class: "recipe" }, h("div", { class: "icon" }), h("div", { class: "name" }, r.name), h("div", { class: "needs" }, "morto"), button);
      }),
    );
  }

  remove(): void {
    this.element.remove();
  }

  private setSolo(solo: boolean): void {
    this.solo = solo;
    this.giveUpArmed = false;
    this.giveUpButton.textContent = "Desistir";
    this.hint.textContent = solo ? SOLO_HINT : ALLY_HINT;
    this.giveUpButton.hidden = !solo;
    this.closeHint.hidden = solo;
  }
}
